import { useCallback, useEffect, useRef, useState } from 'react';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import { PageCard } from '../CardWithTitle';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { CustomizeTableColumns } from '../CustomizeTableColumns/CustomizeTableColumns';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { Account } from '../../../types';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import {
  AccountCustomizationData,
  defaultAccountCustomizationData,
  getAccountCustomizationsById,
  useAccountCustomizationsStore
} from '../../../store/AccountCustomization';
import { GridPreferences, gridInitialState } from '../../../store/User';
import { addError } from '../../../store/Error';
import { PostAccountCustomizationRequest } from '../../../Api/Preferences/AccountCustomizations/accountCustomizationsApi';
import { fetchCustomFieldDefinitionsApi } from '../../../Api/Admin/CustomFields/customFieldDefinitionsApi';
import { DefaultProjectGridColumnsDefs } from '../../Pages/Projects/projectGridColumnsDefs';
import { projectsRelatedInventoryColumns } from '../../Pages/Projects/ProjectsRelatedInventoryGridColumnDefs';
import { GroupedGridColDef } from '../UpstackDataGrid/gridColumnTypes';
import { createCustomFieldColumns } from '../UpstackDataGrid/customFieldColumns';
import { isFieldVisible } from '../UpstackDataGrid/columnVisibility';

const DESCRIPTION =
  'Choose which columns appear in the Projects and Related Inventory tables by default for this account. Users can ' +
  'further adjust their personal view, but this sets the starting layout.';

type TableTabKey = 'project_grid' | 'related_project_assets_grid';

const TAB_LABELS: Record<TableTabKey, string> = {
  project_grid: 'Projects',
  related_project_assets_grid: 'Projects - Related Inventory'
};

const TAB_KEYS: TableTabKey[] = ['project_grid', 'related_project_assets_grid'];

const ENTITY_TYPE_BY_TAB: Record<TableTabKey, string> = {
  project_grid: 'Project',
  related_project_assets_grid: 'ProjectDetail'
};

const TABS: { key: TableTabKey; label: string }[] = TAB_KEYS.map((key) => ({ key, label: TAB_LABELS[key] }));

// The Related Inventory picker must offer the same catalog the runtime grid renders
// (projectsRelatedInventoryColumns) so admins only toggle columns that grid can show. Risk column
// included so it can be configured too. Resolved at call time, not module load:
// projectsRelatedInventoryColumns reads the Inventory catalog, whose module transitively imports this
// one — a module-eval read could capture a mid-cycle subset.
const columnDefsFor = (key: TableTabKey): GroupedGridColDef[] =>
  key === 'related_project_assets_grid' ? projectsRelatedInventoryColumns(true) : DefaultProjectGridColumnsDefs;

const TAB_BASE_STYLE = 'rounded-lg text-sm font-normal py-2 px-4';
const ACTIVE_TAB_STYLE = `${TAB_BASE_STYLE} bg-main-100 text-white`;
const INACTIVE_TAB_STYLE = `${TAB_BASE_STYLE} border border-gray-300 text-gray-700 hover:bg-gray-50`;

const defaultGridFor = (key: TableTabKey): GridPreferences => ({
  muiConfig: gridInitialState(columnDefsFor(key)) as GridInitialStatePro,
  disabledFields: []
});

// What the admin showed or hid, as users see it: the API stamps only these, not the defaults a form fills in.
const switchedColumns = (before: GridPreferences | undefined, after: GridPreferences, columns: GroupedGridColDef[]) =>
  columns
    .filter(
      ({ field, defaultHidden }) =>
        isFieldVisible(field, defaultHidden, [], before?.muiConfig?.columns?.columnVisibilityModel) !==
        isFieldVisible(field, defaultHidden, [], after.muiConfig?.columns?.columnVisibilityModel)
    )
    .map(({ field }) => field);

export interface TableCustomizationsConfigurationFormProps {
  account: Account;
  onClose: () => void;
}

export const TableCustomizationsConfigurationForm = ({
  account,
  onClose
}: TableCustomizationsConfigurationFormProps) => {
  const { setSnack } = useSnackbar();
  const { fetchAccountCustomizations, updateRequest, createRequest } = useAccountCustomizationsStore();
  const [newCustomizations, setNewCustomizations] = useState<AccountCustomizationData | null>(null);
  const [activeTab, setActiveTab] = useState<TableTabKey>('project_grid');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customFieldColumnDefs, setCustomFieldColumnDefs] = useState<Record<TableTabKey, GroupedGridColDef[]>>({
    project_grid: [],
    related_project_assets_grid: []
  });
  // The tables as this form loaded them, before it filled any in: other panels refresh the store meanwhile, and a
  // change made there is no switch of this admin's.
  const storedTablesRef = useRef<Partial<Record<TableTabKey, GridPreferences>>>({});

  const loadConfig = useCallback(async () => {
    setLoading(true);
    const [, projectDefsResult, projectDetailDefsResult] = await Promise.all([
      fetchAccountCustomizations(),
      fetchCustomFieldDefinitionsApi(account.id, undefined, ENTITY_TYPE_BY_TAB.project_grid),
      fetchCustomFieldDefinitionsApi(account.id, undefined, ENTITY_TYPE_BY_TAB.related_project_assets_grid)
    ]);
    setCustomFieldColumnDefs({
      project_grid: createCustomFieldColumns(projectDefsResult.data ?? []),
      related_project_assets_grid: createCustomFieldColumns(projectDetailDefsResult.data ?? [])
    });
    if (projectDefsResult.error) addError('account-customization', projectDefsResult.error.message);
    if (projectDetailDefsResult.error) addError('account-customization', projectDetailDefsResult.error.message);

    const existing = getAccountCustomizationsById(account.id);
    storedTablesRef.current = JSON.parse(JSON.stringify(existing?.content ?? {}));
    const base: AccountCustomizationData = existing
      ? JSON.parse(JSON.stringify(existing))
      : defaultAccountCustomizationData(account.id, account.name);

    // Records saved before this feature existed won't have these keys yet.
    if (!base.content.project_grid) base.content.project_grid = defaultGridFor('project_grid');
    if (!base.content.related_project_assets_grid) {
      base.content.related_project_assets_grid = defaultGridFor('related_project_assets_grid');
    }

    setNewCustomizations(base);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.id]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSetGridPreferences = (tab: TableTabKey, prefs: GridPreferences) => {
    setNewCustomizations((current) => {
      if (!current) return current;
      return { ...current, content: { ...current.content, [tab]: prefs } };
    });
  };

  const canSave = !loading && !saving && !!newCustomizations;

  const handleSave = async () => {
    if (!newCustomizations) return;
    setSaving(true);

    const content = { ...newCustomizations.content };
    TAB_KEYS.forEach((tab) => {
      // An older API may have stored a list, which would otherwise go back as this save's switches.
      const table: GridPreferences = { ...(content[tab] as GridPreferences) };
      delete table.visibilitySwitched;
      const columns = [...columnDefsFor(tab), ...customFieldColumnDefs[tab]];
      const switched = switchedColumns(storedTablesRef.current[tab], table, columns);
      content[tab] = switched.length ? { ...table, visibilitySwitched: switched } : table;
    });
    const req: PostAccountCustomizationRequest = {
      account_id: account.id,
      account_name: account.name,
      content
    };

    const res = newCustomizations.id
      ? await updateRequest(newCustomizations.id, req, 'Projects')
      : await createRequest(req);

    if (res?.error) {
      setSnack({ message: res.error.message, type: 'error', open: true });
    } else {
      setSnack({ message: `Table customizations saved for ${account.name}`, type: 'success', open: true });
      await fetchAccountCustomizations();
      const refreshed = getAccountCustomizationsById(account.id);
      if (refreshed) {
        storedTablesRef.current = JSON.parse(JSON.stringify(refreshed.content));
        setNewCustomizations(JSON.parse(JSON.stringify(refreshed)));
      }
    }

    setSaving(false);
  };

  return (
    <PageCard
      title="Table Customizations"
      dataCy="table-customizations-config-card"
      enableCollapse={false}>
      {loading && <ComponentSpinner sx={{ marginTop: '2rem', marginBottom: '2rem' }} />}
      {!loading && newCustomizations && (
        <>
          <p className="text-sm text-gray-500 mb-4">{DESCRIPTION}</p>
          <div className="flex gap-2 mb-6">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={activeTab === tab.key ? ACTIVE_TAB_STYLE : INACTIVE_TAB_STYLE}
                data-cy={`table-customizations-tab-${tab.key}`}>
                {tab.label}
              </button>
            ))}
          </div>
          <CustomizeTableColumns
            key={activeTab}
            type="account"
            layout="twoPane"
            columnDefs={[...columnDefsFor(activeTab), ...customFieldColumnDefs[activeTab]]}
            gridPreferences={newCustomizations.content[activeTab] as GridPreferences}
            setGridPreferences={(v) => handleSetGridPreferences(activeTab, v as GridPreferences)}
          />
          <div className="flex justify-end gap-3 border-t border-gray-200 pt-6 pr-8 mt-6">
            <button
              onClick={onClose}
              className={DEFAULT_CANCEL_BUTTON_STYLE}
              data-cy="table-customizations-cancel-btn">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave}
              className={DEFAULT_ADVANCE_BUTTON_STYLE}
              data-cy="table-customizations-save-btn">
              Save
            </button>
          </div>
        </>
      )}
    </PageCard>
  );
};
